import type { Dispatch, ReactNode, SetStateAction } from "react"
import { Info, PencilSimple, Warning } from "@phosphor-icons/react"

import { DatePicker, StatusBadge } from "~/components/design-system/components"
import { Popover, PopoverContent, PopoverTrigger } from "~/components/reusable/Popover"
import { StatusColor } from "~/constants/status"
import {
  deriveLifecycleStage,
  formatLifecycleStatus,
  lifecycleDateFieldCopy,
  type LifecycleStage,
  lifecycleDateIssues,
  lifecycleStatusColor,
  noLifecycleDatesSet,
  phasingOutNeverApplies,
} from "~/utils/lifecycleUtils"
import { formatDate, isMondayDatePicker, isSameWeek } from "~/utils/datetime"
import { cn } from "~/utils/helpers"

import { Label } from "../../reusable/Label"
import { Tooltip } from "../../reusable/Tooltip"
import { FieldRow } from "./FieldRow"
import { LifecycleTimeline } from "./LifecycleTimeline"
import { ResetButton } from "./ResetButton"
import type { FieldMetadata, IFormData, MetadataForEdit } from "./types"

const noosOnlyTooltip =
  "Only available for NOOS products. Fashion products are managed through collections."

type DateField = "activatedFrom" | "phasingOutFrom" | "activatedUntil"

const noteTone = {
  info: "border-badge-blue-border bg-badge-blue-bg text-badge-blue-fg-text",
  warning: "border-badge-orange-border bg-badge-orange-bg text-badge-orange-fg-text",
}

const Note = ({ tone, children }: { tone: keyof typeof noteTone; children: ReactNode }) => (
  <div
    className={cn("flex items-start gap-1.5 rounded-md border px-2 py-1.5 text-xs", noteTone[tone])}
  >
    {tone === "info" ? (
      <Info className="w-4 h-4 shrink-0" />
    ) : (
      <Warning className="w-4 h-4 shrink-0" />
    )}
    <span>{children}</span>
  </div>
)

// The server only sends the date in use on V1, where it can differ from the saved date.
const dateInUseIfDifferent = (
  field: FieldMetadata | undefined,
  shown: IFormData["activatedFrom"],
) => {
  const inUse = field?.metadata?.inUseValue
  if (!inUse) return undefined
  const inUseDate = new Date(inUse)
  if (shown && isSameWeek(new Date(shown), inUseDate)) return undefined
  return inUseDate
}

const lifecycleBadge = (formData: IFormData, isMixed: boolean, status: LifecycleStage) => {
  if (isMixed) return { text: "Mixed values", color: lifecycleStatusColor(status) }
  if (noLifecycleDatesSet(formData)) return { text: "No status", color: StatusColor.Neutral }
  return { text: formatLifecycleStatus(status), color: lifecycleStatusColor(status) }
}

const mixedFields = [
  "lifecycleStatus",
  "activatedFrom",
  "activatedUntil",
  "phasingOutFrom",
] as const

type Props = {
  formData: IFormData
  setFormData: Dispatch<SetStateAction<IFormData>>
  metadataForEdit: MetadataForEdit
  isNoos: boolean
  badges: ReactNode
  erpIndicator: ReactNode
  getFieldBadges: (field: keyof IFormData) => ReactNode
  shouldShowResetButton: (field: keyof IFormData) => boolean
  getResetTooltip: (field: keyof IFormData) => string
  handleResetToDefault: (field: keyof IFormData) => void
  /** Opens the date editor on mount, for hosts that send the planner here to set a date. */
  defaultOpen?: boolean
}

export const LifecycleFieldRow = ({
  formData,
  setFormData,
  metadataForEdit,
  isNoos,
  badges,
  erpIndicator,
  getFieldBadges,
  shouldShowResetButton,
  getResetTooltip,
  handleResetToDefault,
  defaultOpen = false,
}: Props) => {
  const isMixed = mixedFields.some(
    (field) => metadataForEdit.fields[field]?.isMultiple && formData[field] === undefined,
  )

  const status = deriveLifecycleStage(formData)
  const badge = lifecycleBadge(formData, isMixed, status)
  const phasingOutDead = phasingOutNeverApplies(formData)
  const dateIssues = lifecycleDateIssues(formData)
  const activatedFromInUse = dateInUseIfDifferent(
    metadataForEdit.fields.activatedFrom,
    formData.activatedFrom,
  )

  const dateField = (
    field: DateField,
    label: string,
    description: string,
    opts?: { noosOnly?: boolean; minDate?: Date; mondaysOnly?: boolean },
  ) => {
    const editable = metadataForEdit.fields[field]?.isEditable && (!opts?.noosOnly || isNoos)
    const value = formData[field]
    const issue = dateIssues[field]

    return (
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center justify-between gap-2">
          <Label className="label-sm font-medium text-fg-base">{label}</Label>
          {getFieldBadges(field)}
        </div>
        <p className="text-xs text-fg-subtle">{description}</p>
        <Tooltip title={opts?.noosOnly && !isNoos ? noosOnlyTooltip : ""}>
          <div className="flex items-center gap-2 w-full">
            <DatePicker
              fullWidth
              value={value ? new Date(value) : undefined}
              onChange={(date) => setFormData((prev) => ({ ...prev, [field]: date }))}
              clearable
              minDate={opts?.minDate}
              filterDate={opts?.mondaysOnly ? isMondayDatePicker : undefined}
              disabled={!editable}
              placeholder={
                metadataForEdit.fields[field]?.isMultiple && value !== null
                  ? "Multiple values"
                  : undefined
              }
            />
            {editable && shouldShowResetButton(field) && (
              <ResetButton
                onClick={() => handleResetToDefault(field)}
                tooltip={getResetTooltip(field)}
              />
            )}
          </div>
        </Tooltip>
        {issue && <Note tone="info">{issue}</Note>}
      </div>
    )
  }

  return (
    // TODO(BUD-370): rename to "Current lifecycle status".
    <FieldRow label="Lifecycle status" badges={badges} erpIndicator={erpIndicator}>
      <Popover defaultOpen={defaultOpen}>
        <PopoverTrigger
          requires="edit"
          allowedTitle="Set the lifecycle dates"
          className="flex h-8 w-full items-center gap-1.5 rounded-md px-1 hover:bg-component-hover"
        >
          <StatusBadge color={badge.color} text={badge.text} />
          <PencilSimple className="w-3.5 h-3.5 shrink-0 text-fg-subtle" />
        </PopoverTrigger>
        <PopoverContent
          align="end"
          className="w-[340px] flex flex-col gap-4"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <div className="flex flex-col gap-2">
            <div className="flex flex-col gap-0.5">
              <span className="label-sm font-medium text-fg-base">Lifecycle status</span>
              <p className="text-xs text-fg-subtle">
                The status is derived from these dates - it cannot be set directly.
              </p>
            </div>
            <LifecycleTimeline
              status={isMixed ? null : status}
              activatedFrom={formData.activatedFrom}
              phasingOutFrom={formData.phasingOutFrom}
              activatedUntil={formData.activatedUntil}
            />
          </div>
          {dateField(
            "activatedFrom",
            "Activated from",
            "First week the product is forecast. Must be a Monday.",
            { noosOnly: true, mondaysOnly: true },
          )}
          {activatedFromInUse && (
            <p className="-mt-2 text-xs text-fg-subtle">
              In use for forecasting: {formatDate(activatedFromInUse)}
            </p>
          )}
          {dateField(
            "phasingOutFrom",
            lifecycleDateFieldCopy.phasingOutFrom.label,
            lifecycleDateFieldCopy.phasingOutFrom.description,
          )}
          {phasingOutDead && (
            <Note tone="warning">
              This phase-out never happens: Activated until ends the variant in an earlier week, so
              it goes straight to Phased out.
            </Note>
          )}
          {dateField(
            "activatedUntil",
            lifecycleDateFieldCopy.activatedUntil.label,
            lifecycleDateFieldCopy.activatedUntil.description,
            {
              minDate: formData.activatedFrom ? new Date(formData.activatedFrom) : undefined,
            },
          )}
        </PopoverContent>
      </Popover>
    </FieldRow>
  )
}
